'use client';

import { useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/src/lib/supabase/client';
import { useBoolean } from '@/src/hooks/use-boolean';
import type { IUIProfile } from '@/src/types/IUIProfile';

type UseNotificationsDotReturn = {
  hasNewDot: ReturnType<typeof useBoolean>;
  refreshDot: () => Promise<void>;
  clearDotOptimistic: () => void;
};

export function useNotificationsDot(profile?: IUIProfile): UseNotificationsDotReturn {
  const hasNewDot = useBoolean(false);
  const mountedRef = useRef(false);

  const refreshDot = useCallback(async () => {
    if (!profile) return;
    if (profile.type === 'anonymous') return;

    const { data, error } = await supabase
      .from('notifications')
      .select('id')
      .eq('owner_id', profile.id)
      .eq('is_new', true)
      .limit(1);

    if (error) return;

    hasNewDot.setValue((data?.length ?? 0) > 0);
  }, [profile?.id, profile?.type]);

  const clearDotOptimistic = useCallback(() => {
    hasNewDot.onFalse();
  }, []);

  useEffect(() => {
    if (!profile) {
      return;
    }
    if (profile.type === 'anonymous') {
      return;
    }

    hasNewDot.setValue(!!profile.isNewNotifications);

    const channel = supabase
      .channel(`rt:notif-dot:${profile.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `owner_id=eq.${profile.id}`,
        },
        (payload) => {
          const row = payload.new as any;
          if (row?.is_new === false) return;
          hasNewDot.onTrue();
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [profile?.id, profile?.type]);

  useEffect(() => {
    if (!profile) return;
    if (profile.type === 'anonymous') return;
    if (mountedRef.current) return;
    mountedRef.current = true;

    void refreshDot();
  }, [profile?.id, profile?.type, refreshDot]);

  return { hasNewDot, refreshDot, clearDotOptimistic };
}
